<template>
  <v-dialog
    v-model="visible"
    fullscreen
    persistent
  >
    <v-card class="bg-super-light-blue">
      <v-container fluid>
        <v-card
          v-if="loaded"
          data-testid="agreement-dialog"
          border
          tile
        >
          <v-card-title class="d-flex align-center text-wrap">
            <span>
              {{ $bct(agreement.name) }}
            </span>

            <v-spacer />

            <StandardButton
              @click="close()"
              aria-label="Close"
              icon="close"
              variant="text"
            />
          </v-card-title>

          <v-divider />

          <template v-if="loaded">
            <v-card-text>
              <v-alert
                v-if="viewerMessage"
                :text="$bct(viewerMessage)"
                class="mb-4"
                data-testid="agreement-viewer-message"
                type="info"
                variant="tonal"
              />

              <QuestionSet
                v-model="agreement"
                :questions="validQuestions"
                :readonly="readonly"
                :schema="schema.definition"
                expanded
                flat
                hide-actions
                outlined-sections
                paddingless
                very-dense
              />

              <div
                v-for="question in verificationQuestions"
                :key="question.id"
              >
                <FormQuestion
                  :color="null"
                  :elevation="0"
                  :subtitle="question.verification_subtitle"
                  :title="question.verification_title"
                  dense
                  hide-actions
                  outlined
                  tile
                  very-dense
                >
                  <template v-if="!readonly">
                    <AttachmentUploader
                      @uploaded="attachments.push($event)"
                      :ref="['uploader', question.id].join('')"
                      :owner="{
                        business_id: $store.state.profile.business_id,
                        type: 'Agreement',
                        id: agreement.id,
                        tag: question.id,
                        tags: [agreement.id, question.id],
                      }"
                      class="mb-4"
                    />
                  </template>

                  <AttachmentList
                    @change="loadAttachments()"
                    @delete="loadAttachments"
                    :attachments="
                      attachments.filter((attachment) => attachment.tags.includes(question.id))
                    "
                    class="mb-6"
                  />
                </FormQuestion>
              </div>

              <UserAttestation
                v-if="hasAttestation"
                @change="isAttested = $event"
                ref="userAttestation"
                :attestation-label="schema.meta.attestation_label"
                :attestation-text="schema.meta.attestation_text"
                :enable-esign="schema.meta.enable_esign"
                :esign-canvas-value="agreement.last_signed_revision?.author_signature_image"
                :esign-name-value="agreement.last_signed_revision?.author_signed_name"
                :mode-value="attestationMode"
                :readonly="readonly"
              />
            </v-card-text>
          </template>

          <template v-else>
            <v-progress-linear indeterminate />
          </template>

          <v-divider />

          <v-card-actions class="d-flex flex-column flex-sm-row ga-3 pa-4">
            <div class="w-100 flex-sm-grow-0">
              <StandardButton
                @click="close"
                :full-width="$vuetify.display.xs"
                :size="'large'"
                class="w-100 w-sm-auto"
                color="primary"
                text="Close"
                variant="outlined"
              />
            </div>

            <v-spacer class="d-none d-sm-flex" />

            <template v-if="!readonly">
              <StandardButton
                @click="saveDraft"
                :full-width="$vuetify.display.xs"
                :loading="processing"
                :size="'large'"
                class="w-100 w-sm-auto"
                color="primary"
                text="Save draft"
                variant="outlined"
              />

              <StandardButton
                v-if="canEditDraft"
                @click="sendAgreement"
                :full-width="$vuetify.display.xs"
                :loading="processing"
                :size="'large'"
                class="w-100 w-sm-auto ml-0 ml-sm-2"
                color="primary"
                data-testid="agreement-send-button"
                text="Send"
              />

              <StandardButton
                v-else
                @click="submit"
                :disabled-text="submitDisabledText"
                :full-width="$vuetify.display.xs"
                :loading="processing"
                :size="'large'"
                class="w-100 w-sm-auto ml-0 ml-sm-2"
                color="primary"
                data-testid="agreement-submit-button"
                text="Finish and submit"
              />
            </template>
          </v-card-actions>
        </v-card>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';

import {
  agreementViewerMessage,
  canEditDraftAgreement,
  isNextAgreementSigner,
} from '@/shared/services/agreement-signing';
import AttachmentList from '@/shared/components/attachments/AttachmentList.vue';
import AttachmentUploader from '@/shared/components/attachments/AttachmentUploader.vue';
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import LinkedList from '@/shared/services/linked-list';
import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import UserAttestation from '@/shared/components/UserAttestation.vue';

const props = defineProps({
  role: {
    type: String,
    default: 'member',
  },
});

const emit = defineEmits(['change']);

defineExpose({ open });

const $bct = inject('translator');

const eventBus = useEventBus();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { api: Api } = useApi();

const agreement = ref(null);
const attachments = ref([]);
const isAttested = ref(null);
const loaded = ref(false);
const processing = ref(false);
const questions = ref([]);
const schema = ref(null);
const userAttestation = ref(null);
const visible = ref(false);

const attestationMode = computed(() => {
  if (agreement.value.last_signed_revision?.author_signature_image) return 'draw';

  return 'type';
});

const hasAttestation = computed(() => {
  return schema.value?.meta?.enable_attestation && !schema.value?.meta?.second_signer_role;
});

const incompleteQuestions = computed(() => {
  return mandatoryQuestions.value.filter((question) =>
    question.synced_attributes.some((attribute) => {
      const attrParts = attribute.name.split('.');
      return !agreement.value.custom[attrParts[attrParts.length - 1]];
    }),
  );
});

const incompleteVerificationQuestions = computed(() => {
  return mandatoryQuestions.value
    .filter((question) => question.verification)
    .filter(
      (question) =>
        attachments.value.filter((attachment) => attachment.tags.includes(question.id)).length ===
        0,
    );
});

const completed = computed(() => {
  const attestationRequired = hasAttestation.value && !isAttested.value;

  return (
    incompleteQuestions.value.length === 0 &&
    incompleteVerificationQuestions.value.length === 0 &&
    !attestationRequired
  );
});

const submitDisabledText = computed(() => {
  if (completed.value) return null;

  return 'Please complete all required fields before submitting';
});

const mandatoryQuestions = computed(() => {
  return validQuestions.value.filter((question) => question.mandatory);
});

const canSign = computed(() => isNextAgreementSigner(agreement.value, store.state.profile?.id));

const canEditDraft = computed(() => canEditDraftAgreement(agreement.value, props.role));

const readonly = computed(() => !canSign.value && !canEditDraft.value);

const viewerMessage = computed(() =>
  agreementViewerMessage(agreement.value, store.state.profile?.id, { role: props.role }),
);

const verificationQuestions = computed(() => {
  return LinkedList.sort(questions.value)
    .filter((question) => question.verification)
    .filter((question) => question.published && question.valid);
});

const validQuestions = computed(() => {
  return LinkedList.sort(questions.value).filter((question) => question.valid);
});

// TODO: Remove and call open() directly
watch(
  () => route?.query?.agreementId,
  async (newVal) => {
    if (newVal) {
      await open();
    } else {
      await close();
    }
  },
  { immediate: true },
);

function agreementApi() {
  switch (props.role) {
    case 'organization':
      return Api.organization.agreement;
    case 'manager':
      return Api.manager.agreement;
    case 'parent':
      return Api.parent.agreement;
    default:
      return {};
  }
}

function attachmentApi() {
  switch (props.role) {
    case 'organization':
      return Api.organization.attachment;
    case 'manager':
      return Api.manager.attachment;
    case 'parent':
      return Api.member.attachment;
    default:
      return {};
  }
}

async function clearAgreementRoute() {
  if (!route?.query?.agreementId) return;

  const query = { ...route.query };
  delete query.agreementId;

  if (!query.tab) {
    query.tab = 'forms-and-agreements';
  }

  await router.push({ query });
}

async function close() {
  await clearAgreementRoute();
  visible.value = false;
  resetAgreement();
}

async function open(agreementId, _options = {}) {
  resetAgreement();

  const id = agreementId || route?.query?.agreementId;
  const response = await agreementApi().get(id);
  if (!response?.data) return;

  agreement.value = response.data;

  schema.value = store.state.schemas[agreement.value.schema_id];
  if (!schema.value && agreement.value.schema_id) await loadSchema(agreement.value.schema_id);

  await loadAttachments();
  await loadQuestions();

  if (!schema.value) {
    resetAgreement();
    return;
  }

  visible.value = true;
  loaded.value = true;
}

function resetAgreement() {
  agreement.value = null;
  loaded.value = false;
  schema.value = null;
  questions.value = [];
}

async function saveDraft() {
  processing.value = true;
  agreement.value.submit_requested = false;

  const response = await agreementApi().update(agreement.value.id, agreement.value);
  agreement.value = response.data;
  processing.value = false;
  eventBus.chime('Saved');
}

async function sendAgreement() {
  processing.value = true;

  const response = await agreementApi().update(agreement.value.id, {
    ...agreement.value,
    send_requested: true,
  });
  processing.value = false;

  if (response?.status !== 200) return;

  agreement.value = response.data;
  eventBus.chime('Sent');
  emit('change');
  await close();
}

async function submit() {
  processing.value = true;

  const submitParams = agreement.value;
  submitParams.submit_requested = true;

  if (hasAttestation.value) {
    submitParams.revision_author_signed_name = userAttestation.value?.esignName;
    submitParams.revision_author_signature_image = userAttestation.value?.getEsignSignature();

    const attestationText = userAttestation.value?.getAttestationText();
    if (attestationText) submitParams.revision_description = attestationText;
  }

  const response = await agreementApi().update(agreement.value.id, agreement.value);
  processing.value = false;

  if (response?.status !== 200) return;

  eventBus.chime('Agreement submitted');
  emit('change');
  await close();
}

async function validate() {
  const assertions = questions.value.map((question) => {
    if (question.conditions.length > 0) {
      return {
        assertion: {
          conditions: question.conditions,
          value: this[question.synced_model.toLowerCase()],
        },
      };
    }
    return { assertion: { conditions: [], value: {} } };
  });

  const response = await Api.publicApi.assertion.promiseBulkCreate(assertions);
  const results = response?.data?.results;

  questions.value.forEach((question, index) => {
    // eslint-disable-next-line no-param-reassign
    question.valid = results[index].result;
  });
}

async function loadAttachments() {
  const response = await attachmentApi().index({
    owner_id: agreement.value.id,
    owner_type: 'Agreement',
  });
  if (response?.status !== 200) return;

  attachments.value = response.data;
}

async function loadQuestions() {
  if (!schema.value) {
    eventBus.error('Agreement not found');
    return;
  }

  const response = await Api.publicApi.organization.question.index({
    owner_id: schema.value.id,
    owner_type: 'Schema',
  });

  if (!response?.data) return;

  questions.value = response.data || [];
  await validate();
}

async function loadSchema(id) {
  const response = await Api.publicApi.organization.schema.get(id);
  schema.value = response?.data;
}
</script>
